import { useId, useMemo, useRef, useState } from 'react';
import {
  apiErrorText,
  useApplyMtoReceiptsImportMutation,
  usePreviewMtoReceiptsImportMutation,
} from '@shared/api';
import { pluralizeRu } from '@shared/lib/formatFile';
import { Button } from '@shared/ui/Button';
import {
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerFooter,
  DrawerHeader,
} from '@shared/ui/Drawer';
import {
  Actions,
  CheckLabel,
  Control,
  FieldHint,
  FieldLabel,
  FleetChip,
  FooterStatus,
  Notice,
  Spacer,
  typo,
} from '@entities/fleet';
import {
  MATERIAL_KIND_LABEL,
  formatDate,
  formatMoney0,
  type ReceiptsPreview,
} from '@entities/mto/receipts';
import { DrawerTitle, Eyebrow, HeaderRow, Titles } from './drawerParts';
import { Section, Table, TableBox } from './styles';

const ROWS = ['строка', 'строки', 'строк'] as const;

/**
 * Загрузка книги «Приход ТМЦ» в два шага: разбор показывает листы, суммы,
 * замечания и новые написания объектов, ничего не записывая; «Загрузить»
 * отправляет тот же файл (сверяется SHA-256) и выбранные листы. Строки листа
 * из прошлой загрузки заменяются, исправленные в ОСНОВЕ — остаются.
 */
export function ImportDrawer({
  onClose,
  onDone,
}: {
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const uid = useId();
  const titleRef = useRef<HTMLHeadingElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ReceiptsPreview | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [parse, parseState] = usePreviewMtoReceiptsImportMutation();
  const [apply, applyState] = useApplyMtoReceiptsImportMutation();
  const busy = parseState.isLoading || applyState.isLoading;

  const loadable = useMemo(() => (preview?.sheets ?? []).filter((s) => s.rows > 0), [preview]);
  const chosen = loadable.filter((s) => selected.has(s.name));
  const chosenRows = chosen.reduce((n, s) => n + s.rows, 0);
  const newObjects = useMemo(() => (preview?.objects ?? []).filter((o) => !o.known), [preview]);

  async function onFile(f: File | null) {
    setFile(f);
    setPreview(null);
    setError(null);
    if (!f) return;
    const form = new FormData();
    form.append('file', f);
    try {
      const res = await parse(form).unwrap();
      setPreview(res.preview);
      setSelected(new Set(res.preview.sheets.filter((s) => s.rows > 0).map((s) => s.name)));
    } catch (err) {
      setError(apiErrorText(err, 'Книгу не удалось разобрать.'));
    }
  }

  async function onApply() {
    if (!file || !preview || chosen.length === 0) return;
    setError(null);
    const form = new FormData();
    form.append('file', file);
    form.append('sha256', preview.sha256);
    form.append('sheets', chosen.map((s) => s.name).join('\n'));
    try {
      const { result } = await apply(form).unwrap();
      const parts = [
        `Загружено ${result.inserted.toLocaleString('ru-RU')} ${pluralizeRu(result.inserted, ROWS)}`,
      ];
      if (result.replaced > 0)
        parts.push(`заменено прежних — ${result.replaced.toLocaleString('ru-RU')}`);
      const kept = result.sheets.reduce((n, s) => n + s.kept_edited, 0);
      if (kept > 0) parts.push(`исправленные в ОСНОВЕ оставлены — ${kept}`);
      if (result.new_objects > 0)
        parts.push(`новых объектов — ${result.new_objects}, их видно на вкладке «Объекты прихода»`);
      onDone(`${parts.join(', ')}.`);
    } catch (err) {
      setError(apiErrorText(err, 'Книгу не удалось загрузить.'));
    }
  }

  function toggle(name: string, on: boolean) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(name);
      else next.delete(name);
      return next;
    });
  }

  return (
    <Drawer open onClose={onClose} labelledBy={`${uid}-title`} initialFocusRef={titleRef} size="lg">
      <DrawerHeader>
        <HeaderRow>
          <Titles>
            <Eyebrow>Приход ТМЦ</Eyebrow>
            <DrawerTitle id={`${uid}-title`} ref={titleRef} tabIndex={-1}>
              Загрузить книгу Excel
            </DrawerTitle>
          </Titles>
          <DrawerCloseButton onClick={onClose} />
        </HeaderRow>
      </DrawerHeader>
      <DrawerBody>
        <FieldLabel htmlFor={`${uid}-file`}>
          <span>Книга «Приход ТМЦ» (.xlsx)</span>
        </FieldLabel>
        <Control
          id={`${uid}-file`}
          type="file"
          accept=".xlsx,.xlsm"
          disabled={busy}
          aria-describedby={`${uid}-file-hint`}
          onChange={(e) => void onFile(e.target.files?.[0] ?? null)}
        />
        <FieldHint id={`${uid}-file-hint`}>
          {typo(
            'Лист на год, шапка «Дата, Поставщик, Наименование, Сумма, Объект…» — как в книге отдела МТО. Сначала программа покажет, что поняла; в журнал ничего не запишется, пока вы не нажмёте «Загрузить».',
          )}
        </FieldHint>

        {parseState.isLoading && (
          <Notice $tone="info" role="status">
            <span className="text">Разбираю книгу — большая книга читается до минуты…</span>
          </Notice>
        )}
        {error && (
          <Notice $tone="danger" role="alert">
            <span className="text">{typo(error)}</span>
          </Notice>
        )}

        {preview && (
          <>
            {preview.warnings.map((w) => (
              <Notice key={w} $tone="warning" role="note">
                <span className="text">{typo(w)}</span>
              </Notice>
            ))}
            <Section aria-labelledby={`${uid}-sheets`}>
              <h3 id={`${uid}-sheets`}>
                Листы: {preview.rows.toLocaleString('ru-RU')} {pluralizeRu(preview.rows, ROWS)},{' '}
                {formatMoney0(preview.amount)} ₽ без НДС
              </h3>
              <TableBox>
                <Table>
                  <thead>
                    <tr>
                      <th scope="col">Лист</th>
                      <th scope="col" className="num">
                        Строк
                      </th>
                      <th scope="col">Даты</th>
                      <th scope="col" className="num">
                        Без НДС
                      </th>
                      <th scope="col" className="num">
                        С НДС
                      </th>
                      <th scope="col">Что будет</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.sheets.map((s) => (
                      <tr key={s.name}>
                        <td>
                          {s.rows > 0 ? (
                            <CheckLabel>
                              <input
                                type="checkbox"
                                checked={selected.has(s.name)}
                                disabled={busy}
                                onChange={(e) => toggle(s.name, e.target.checked)}
                              />
                              {s.name}
                            </CheckLabel>
                          ) : (
                            s.name
                          )}
                          {s.note && <div className="sub">{s.note}</div>}
                        </td>
                        <td className="num">{s.rows.toLocaleString('ru-RU')}</td>
                        <td className="nowrap">
                          {s.date_from
                            ? `${formatDate(s.date_from)} – ${formatDate(s.date_to)}`
                            : '—'}
                        </td>
                        <td className="num">{formatMoney0(s.amount)}</td>
                        <td className="num">{formatMoney0(s.amount_with_vat)}</td>
                        <td>
                          {s.rows === 0
                            ? 'не загружается'
                            : s.imported > 0
                              ? `заменит ${s.imported - s.edited} из ${s.imported} загруженных раньше${s.edited > 0 ? `; ${s.edited} исправленных в ОСНОВЕ останутся` : ''}`
                              : 'новые строки'}
                          {s.issues > 0 && (
                            <div className="warn">
                              с замечаниями: {s.issues}
                              {s.skipped > 0 && `, без даты пропущено: ${s.skipped}`}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </TableBox>
            </Section>

            {preview.sheets.some((s) => s.issue_samples.length > 0) && (
              <Section aria-labelledby={`${uid}-issues`}>
                <h3 id={`${uid}-issues`}>Замечания разбора</h3>
                <p>
                  {typo(
                    'Строки с замечаниями тоже загружаются — с пометкой, их можно найти в журнале флажком «С замечаниями» и поправить. Сумма, которую не удалось прочитать, не превращается в ноль.',
                  )}
                </p>
                {preview.sheets
                  .filter((s) => s.issue_samples.length > 0)
                  .map((s) => (
                    <details key={s.name}>
                      <summary>
                        Лист {s.name}: {s.issues} {pluralizeRu(s.issues, ROWS)}
                      </summary>
                      <ul>
                        {s.issue_samples.map((i) => (
                          <li key={`${i.row}-${i.text}`}>
                            строка {i.row}: {i.text}
                          </li>
                        ))}
                      </ul>
                    </details>
                  ))}
              </Section>
            )}

            <Section aria-labelledby={`${uid}-objects`}>
              <h3 id={`${uid}-objects`}>
                Объекты: {preview.objects.length}, новых — {newObjects.length}
              </h3>
              <p>
                {typo(
                  'Новые написания объектов заведутся сами, программа предложит, к какому объекту или подразделению их отнести. Проверить и поправить — на вкладке «Объекты прихода».',
                )}
              </p>
              {newObjects.length > 0 && (
                <ul>
                  {newObjects.slice(0, 40).map((o) => (
                    <li key={o.key}>
                      {o.name} — {o.rows.toLocaleString('ru-RU')} {pluralizeRu(o.rows, ROWS)},{' '}
                      {formatMoney0(o.amount)} ₽{' '}
                      <FleetChip $tone="muted">
                        {MATERIAL_KIND_LABEL[o.material_kind].toLowerCase()}
                      </FleetChip>
                    </li>
                  ))}
                  {newObjects.length > 40 && <li>…и ещё {newObjects.length - 40}</li>}
                </ul>
              )}
            </Section>
          </>
        )}
      </DrawerBody>
      <DrawerFooter>
        <FooterStatus role="status">
          {applyState.isLoading
            ? 'Загружаю…'
            : preview
              ? `Выбрано листов: ${chosen.length}, строк: ${chosenRows.toLocaleString('ru-RU')}`
              : ''}
        </FooterStatus>
        <Spacer />
        <Actions>
          <Button type="button" $variant="ghost" disabled={applyState.isLoading} onClick={onClose}>
            Отмена
          </Button>
          <Button
            type="button"
            $variant="primary"
            disabled={busy || !preview || chosen.length === 0}
            onClick={() => void onApply()}
          >
            Загрузить
          </Button>
        </Actions>
      </DrawerFooter>
    </Drawer>
  );
}
